/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */

import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
  Row, Col, Input, InputGroup,
  InputGroupAddon, ListGroup, ListGroupItem, Label,
} from 'reactstrap';
import { Field, Button } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startCase } from 'lodash';
import dateFormats from './date-formats';

const aggregationTypeOptions = [{
  label: 'Sum',
  value: 'sum',
}, {
  label: 'Count',
  value: 'count',
}, {
  label: 'Unique Count',
  value: 'unique-count',
}, {
  label: 'Average',
  value: 'average',
}];

const chartTypeOptions = [
  'pie',
  'doughnut',
  'bar',
  'line',
  'heatmap',
].map(label => ({
  label,
  value: label,
}));

const yTickFormatOptions = [
  'percent',
  'integer',
  'decimal',
].map(label => ({
  label,
  value: label,
}));

export const dataLabelValueOptions = [
  'value',
  'percentage',
].map(value => ({
  label: startCase(value),
  value,
}));

const timeUnitOptions = [
  'year',
  'month',
  'week',
  'day',
].map(value => ({
  label: startCase(value),
  value,
}));

const legendPositionOptions = [
  'bottom',
  'right',
].map(label => ({
  label,
  value: label,
}));

const CategoryOrderEditor = ({ name }) => {
  const { values, setFieldValue } = useFormikContext();
  const [newItem, setNewItem] = useState('');

  const items = name.split('.').reduce((obj, key) => obj?.[key], values) || [];

  const handleAdd = () => {
    const trimmed = newItem.trim();
    if (!trimmed || items.includes(trimmed)) return;
    setFieldValue(name, [...items, trimmed]);
    setNewItem('');
  };

  const handleRemove = (idx) => {
    setFieldValue(name, items.filter((_, i) => i !== idx));
  };

  const handleMove = (idx, direction) => {
    const newItems = [...items];
    const targetIdx = idx + direction;
    [newItems[idx], newItems[targetIdx]] = [newItems[targetIdx], newItems[idx]];
    setFieldValue(name, newItems);
  };

  return (
    <div>
      <Label className="text-primary">Category Order</Label>
      <small className="text-muted d-block mb-2">
        Define the display order of chart categories.
        Values not listed here appear at the end in
        natural sort order.
      </small>

      <InputGroup className="mb-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Type a category value and press Enter or Add"
        />
        <InputGroupAddon addonType="append">
          <Button type="button" color="primary" onClick={handleAdd} disabled={!newItem.trim()}>
            <FontAwesomeIcon icon="plus" className="mr-1" />
            Add
          </Button>
        </InputGroupAddon>
      </InputGroup>

      {items.length > 0 && (
        <ListGroup className="mb-2">
          {items.map((item, i) => (
            <ListGroupItem key={i} className="d-flex align-items-center py-1 px-2">
              <div className="mr-2" style={{ width: '32px' }}>
                <Row form style={{ height: '50%' }}>
                  <Button
                    className="m-auto p-0 border-0"
                    style={{ width: '32px' }}
                    outline
                    type="button"
                    onClick={() => handleMove(i, -1)}
                    disabled={i === 0}
                  >
                    <FontAwesomeIcon icon="caret-up" />
                  </Button>
                </Row>
                <Row form style={{ height: '50%' }}>
                  <Button
                    className="m-auto p-0 border-0"
                    style={{ width: '32px' }}
                    outline
                    type="button"
                    onClick={() => handleMove(i, 1)}
                    disabled={i === items.length - 1}
                  >
                    <FontAwesomeIcon icon="caret-down" />
                  </Button>
                </Row>
              </div>
              <span className="flex-grow-1">{item}</span>
              <Button
                size="sm"
                outline
                color="danger"
                type="button"
                onClick={() => handleRemove(i)}
                className="p-0 px-1 border-0"
              >
                <FontAwesomeIcon icon="times" />
              </Button>
            </ListGroupItem>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

const ChartForm = ({
  index,
  availableReports,
}) => {
  const {
    values,
    setFieldError,
    setFieldTouched,
    setFieldValue,
  } = useFormikContext();

  useEffect(() => {
    if (values.charts[index].isNew) {
      setFieldValue(`charts.${index}.shouldShowLegend`, true);
      setFieldValue(`charts.${index}.legendPosition`, legendPositionOptions.find(lpo => lpo.value === 'bottom'));
      setFieldValue(`charts.${index}.yTickFormat`, yTickFormatOptions.find(ytfo => ytfo.value === 'integer'));
      setFieldValue(`charts.${index}.usesSameView`, true);
      setFieldValue(`charts.${index}.displayDataLabels`, true);
      setFieldValue(`charts.${index}.dataLabelValue`, dataLabelValueOptions.find(o => o.value === 'percentage'));
      setFieldValue(`charts.${index}.showNulls`, true);
      setFieldValue(`charts.${index}.nullText`, 'Unknown');
      setFieldValue(`charts.${index}.categoryOrder`, []);
    }
  }, []);

  const [columnOptions, setColumnOptions] = useState([]);

  useEffect(() => {
    const reportView = values.charts[index].reportView || values.reportView;

    if (reportView) {
      setColumnOptions(reportView.columns);

      if (values.charts[index].columnName) {
        // if the user imports an exported configuration, and any of the
        // columns in the report view have had their names changed, the report
        // will fail to save because of the discrepancy. show an error to the
        // user to alert them to this
        if (!reportView.columns.some(c => c.value === values.charts[index].columnName.value)) {
          const field = `charts.${index}.columnName`;
          const errorMessage = `A column named "${values.charts[index].columnName.value}" is not present in ${reportView.value}. Please re-select the correct column.`;

          setFieldError(field, errorMessage);
          setFieldTouched(field, true);
        }
      }
    }
  }, [values.reportView, values.charts, index]);

  const [chartType, setChartType] = useState('');

  useEffect(() => {
    setChartType(values.charts[index].type ? values.charts[index].type.value : '');
  }, [values.charts[index].type]);

  const [oldValues, setOldValues] = useState(values);

  useEffect(() => {
    // when time unit changes, auto-set x tick format to first option
    if (values.charts[index].timeUnit !== oldValues.charts[index].timeUnit) {
      setFieldValue(
        `charts.${index}.xTickFormat`,
        dateFormats[values.charts[index].timeUnit ? values.charts[index].timeUnit.value : 'day'][0],
      );
    }

    setOldValues(values);
  }, [values.charts[index].timeUnit]);

  return (<>
    <Row form>
      <Col sm>
        <Field
          name={`charts.${index}.title`}
          label="Chart Title"
        />
      </Col>

      <Col sm>
        <Field
          name={`charts.${index}.type`}
          label="Chart Type"
          type="select"
          options={chartTypeOptions}
          required
        />
      </Col>

      {chartType === 'bar' && (<>
        <Col sm>
          <Field
            name={`charts.${index}.isStacked`}
            label="Stack Bars?"
            type="checkbox"
            text="If more than one bar appears per tick of the X-axis, stack the bars on top of each other"
          />
        </Col>
      </>)}

      {chartType === 'line' && (<>
        <Col sm>
          <Field
            name={`charts.${index}.isFilledLine`}
            label="Fill in lines?"
            type="checkbox"
            text="If checked, the area under the lines will be colored in"
          />
        </Col>
      </>)}

      <Col sm>
        <Field
          name={`charts.${index}.usesSameView`}
          label="Chart uses same view as rest of report"
          type="checkbox"
        />
      </Col>

      {!values.charts[index].usesSameView && (
        <Col sm>
          <Field
            name={`charts.${index}.reportView`}
            label="Report View"
            text="If left blank, will use base report view"
            type="select"
            options={availableReports}
            required
          />
        </Col>
      )}
    </Row>

    {['line', 'bar'].includes(chartType) ? (<>
      <Row form>
        <Col sm>
          <Field
            name={`charts.${index}.isTimeSeries`}
            label="Is Time Series?"
            type="checkbox"
          />
        </Col>

        {values.charts[index].isTimeSeries && (<>
          <Col sm>
            <Field
              name={`charts.${index}.timeUnit`}
              label="Time Unit"
              type="select"
              options={timeUnitOptions}
              required
            />
          </Col>
        </>)}
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`charts.${index}.xAxisColumn`}
            label="X-axis Column"
            type="select"
            options={columnOptions}
            text={<>
              <p className="m-0">Column representing values on the HORIZONTAL axis (not needed for most simple bar charts)</p>
              {values.charts[index].isTimeSeries && (
                <p className="m-0">Since this chart is a timeseries, this should probably be a date or time column.</p>
              )}
            </>}
            required={values.charts[index].isTimeSeries || chartType === 'line'}
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.xAxisColumnDBFormat`}
            label="Format of Column in DB"
            text="What is the date format of the column in the DB? Not usually needed unless, for example, a column only returns a month instead of month/day/year"
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.xTickFormat`}
            label="X-axis Tick Format"
            type="select"
            options={dateFormats[values.charts[index].timeUnit ? values.charts[index].timeUnit.value : 'day']}
            getOptionLabel={df => (<><span>{df.value}</span> <span style={{ color: 'gray' }}>({df.label})</span></>)}
            required={values.charts[index].isTimeSeries}
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.aggregationType`}
            label="Aggregation Type"
            text={<>
              <p className="m-0"><span className="font-weight-bold">Sum</span>: Add up non-null numerical values in each vertical column</p>
              <p className="m-0"><span className="font-weight-bold">Count</span>: Count non-null values in each vertical column</p>
              <p className="m-0"><span className="font-weight-bold">Unique Count</span>: Count unique non-null values in each vertical column</p>
              <p className="m-0"><span className="font-weight-bold">Average</span>: Calculate the average of non-null numerical values in each vertical column</p>
            </>}
            type="select"
            options={aggregationTypeOptions}
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`charts.${index}.yAxisColumn`}
            label="Y-axis Column"
            type="select"
            multi
            options={columnOptions}
            text="Column(s) representing values on the VERTICAL axis"
            required
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.yTickFormat`}
            label="Y-axis Tick Format"
            type="select"
            options={yTickFormatOptions}
            text="If percent or decimal, will be rounded to nearest hundredth"
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.yTickMin`}
            label="Y-axis Minimum Value"
            type="number"
          />
        </Col>

        <Col sm>
          <Field
            name={`charts.${index}.yTickMax`}
            label="Y-axis Maximum Value"
            type="number"
          />
        </Col>
      </Row>
    </>) : chartType === 'heatmap' ? (
        <>
          <Row form>
            <Col sm>
              <Field
                name={`charts.${index}.latitudeColumn`}
                label="View Latitude Column"
                text="The latitude mapping of this report view"
                type='select'
                options={columnOptions}
                required
              />
            </Col>
            <Col sm>
              <Field
                name={`charts.${index}.longitudeColumn`}
                label="View Longitude Column"
                text="The longitude mapping of this report view"
                type='select'
                options={columnOptions}
                required
              />
            </Col>
          </Row>
        </>
      ) : (<>
      <Row form>
        <Col sm>
          <Field
            name={`charts.${index}.columnName`}
            label="View Column Name"
            text="The column in the report view"
            type="select"
            options={columnOptions}
            required
          />
        </Col>
      </Row>
    </>)}

    {chartType && !values.charts[index].isTimeSeries && (
      <Row form>
        <Col sm>
          <CategoryOrderEditor name={`charts.${index}.categoryOrder`} />
        </Col>
      </Row>
    )}

    <Row form>
      <Col sm>
        <Field
          name={`charts.${index}.showNulls`}
          label="Show null values?"
          type="checkbox"
        />
      </Col>

      {values.charts[index].showNulls && (
        <Col sm>
          <Field
            name={`charts.${index}.nullText`}
            label="Null value text"
            text="This text will replace 'null' in the chart. If this matches another value shown in the chart, the data for nulls will be combined with the data for that value."
          />
        </Col>
      )}
    </Row>

    <Row form>
      <Col sm>
        <Field
          name={`charts.${index}.displayDataLabels`}
          label="Show data labels?"
          type="checkbox"
        />
      </Col>

      {values.charts[index].displayDataLabels && (
        <Col sm>
          <Field
            name={`charts.${index}.dataLabelValue`}
            label="Data label value"
            type="select"
            options={dataLabelValueOptions}
            text="If value, chart segments will display the raw value. If percentage, chart segments will display the percentage of the total that the raw value represents."
          />
        </Col>
      )}
    </Row>

    <Row form>
      <Col sm>
        <Field
          name={`charts.${index}.shouldShowLegend`}
          label="Show Legend?"
          type="checkbox"
        />
      </Col>

      {values.charts[index].shouldShowLegend && (
        <Col sm>
          <Field
            name={`charts.${index}.legendPosition`}
            label="Legend Position"
            type="select"
            options={legendPositionOptions}
            required={values.charts[index].shouldShowLegend}
          />
        </Col>
      )}
    </Row>
  </>);
};

export default ChartForm;
